import React, { useState, useEffect } from "react";
import { collection, query, orderBy, onSnapshot, doc, deleteDoc, updateDoc } from "firebase/firestore";
import { onAuthStateChanged } from "firebase/auth";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { db, auth, storage } from "../firebase/config";
import { FEE_SCHEDULE_APPROVERS } from "./admin/constants";
import { getEnrollmentType } from "./admin/helpers";
import { generateSignedPdfForRecord } from "./admin/pdf";
import DashboardStats from "./admin/DashboardStats";
import DashboardControls from "./admin/DashboardControls";
import SubmissionsTable from "./admin/SubmissionsTable";
import ApplicationDetailsModal from "./admin/ApplicationDetailsModal";
import DocumentsModal from "./admin/DocumentsModal";
import ManualEntryModal from "./admin/ManualEntryModal";
import ActivityLogModal from "./admin/ActivityLogModal";
import "./AdminDashboard.css";

// Orchestrator: owns the live submissions feed, filtering, which modal is
// open, and cross-cutting actions (delete, fee approval, PDF backfill).
// Everything else lives in the components under ./admin/.
const AdminDashboard = ({ onLogout }) => {
    const [submissions, setSubmissions] = useState([]);
    const [loading, setLoading] = useState(true);
    const [filter, setFilter] = useState("all");
    const [enrollmentFilter, setEnrollmentFilter] = useState("all");
    const [searchTerm, setSearchTerm] = useState("");
    const [selectedSubmission, setSelectedSubmission] = useState(null);
    const [documentsModal, setDocumentsModal] = useState(null);
    const [activityModal, setActivityModal] = useState(null);
    const [showManualEntry, setShowManualEntry] = useState(false);
    const [backfillStatus, setBackfillStatus] = useState({ running: false, progress: '', count: 0, total: 0 });
    const [toast, setToast] = useState(null);

    // Fee schedule approval state
    const [approvingFeeSchedule, setApprovingFeeSchedule] = useState(null);
    const currentAdminEmail = localStorage.getItem("admin_email")?.toLowerCase() || localStorage.getItem("admin_remembered_email")?.toLowerCase() || "";
    const canApproveFeeSchedule = FEE_SCHEDULE_APPROVERS.includes(currentAdminEmail);

    useEffect(() => {
        let unsubFirestore = null;

        const unsubAuth = onAuthStateChanged(auth, (user) => {
            if (unsubFirestore) {
                unsubFirestore();
                unsubFirestore = null;
            }

            if (user) {
                const q = query(collection(db, "medical_providers"), orderBy("timestamp", "desc"));
                unsubFirestore = onSnapshot(q, (snapshot) => {
                    const data = snapshot.docs.map((doc) => ({
                        id: doc.id,
                        ...doc.data(),
                    }));
                    setSubmissions(data);
                    setLoading(false);
                }, (error) => {
                    console.error("Firestore access denied:", error);
                    setLoading(false);
                });
            } else {
                setSubmissions([]);
                setLoading(false);
                if (localStorage.getItem("admin_authenticated") === "true") {
                    localStorage.removeItem("admin_authenticated");
                    localStorage.removeItem("admin_email");
                    window.location.href = "/admin";
                }
            }
        });

        return () => {
            unsubAuth();
            if (unsubFirestore) unsubFirestore();
        };
    }, []);

    // Keep the open activity modal in sync with live Firestore updates
    // (e.g. the Cloud Function flipping emailSent, or documents uploading),
    // so checklist auto-items reflect reality without reopening the modal.
    useEffect(() => {
        setActivityModal(prev => {
            if (!prev) return prev;
            const fresh = submissions.find(s => s.id === prev.id);
            return fresh || prev;
        });
    }, [submissions]);

    const filteredSubmissions = submissions.filter((submission) => {
        const matchesFilter = filter === "all" || submission.specialty === filter;
        const matchesEnrollment = enrollmentFilter === "all" || getEnrollmentType(submission) === enrollmentFilter;
        const matchesSearch = searchTerm === "" || submission.name?.toLowerCase().includes(searchTerm.toLowerCase()) || submission.email?.toLowerCase().includes(searchTerm.toLowerCase()) || submission.practiceName?.toLowerCase().includes(searchTerm.toLowerCase());

        return matchesFilter && matchesEnrollment && matchesSearch;
    });

    // Show toast notification
    const showToast = (message, type = 'success') => {
        setToast({ message, type });
        setTimeout(() => setToast(null), 3000);
    };

    const handleViewDetails = (submission) => {
        setSelectedSubmission(submission);
    };

    const closeModal = () => {
        setSelectedSubmission(null);
    };

    const openActivityModal = (submission) => {
        setActivityModal(submission);
    };

    const closeActivityModal = () => {
        setActivityModal(null);
    };

    const handleDelete = async (submission) => {
        const confirmed = window.confirm(
            `Are you sure you want to delete the application from ${submission.name || "this applicant"}?\n\nThis action cannot be undone.`
        );

        if (confirmed) {
            try {
                await deleteDoc(doc(db, "medical_providers", submission.id));
                // Close modal if the deleted submission was being viewed
                if (selectedSubmission?.id === submission.id) {
                    setSelectedSubmission(null);
                }
            } catch (error) {
                console.error("Error deleting submission:", error);
                alert("Failed to delete the application. Please try again.");
            }
        }
    };

    // Handle fee schedule approval (only for authorized users)
    const handleFeeScheduleApproval = async (submission, isApproved) => {
        if (!canApproveFeeSchedule) return;

        setApprovingFeeSchedule(submission.id);
        try {
            const docRef = doc(db, "medical_providers", submission.id);
            const approverName = currentAdminEmail.split('@')[0].replace('.', ' ').replace(/\b\w/g, c => c.toUpperCase());

            await updateDoc(docRef, {
                feeScheduleApproved: isApproved,
                feeScheduleApprovedBy: isApproved ? currentAdminEmail : null,
                feeScheduleApprovedByName: isApproved ? approverName : null,
                feeScheduleApprovedAt: isApproved ? new Date() : null,
            });

            showToast(isApproved ? "Fee schedule approved" : "Fee schedule approval removed");
        } catch (error) {
            console.error("Error updating fee schedule approval:", error);
            showToast("Failed to update. Please try again.", "error");
        } finally {
            setApprovingFeeSchedule(null);
        }
    };

    const handleBackfillSignedPdfs = async () => {
        const recordsToBackfill = submissions.filter(s => s.signature && !s.signedPdfUrl);
        const recordsAlreadyHavePdf = submissions.filter(s => s.signedPdfUrl).length;

        if (recordsToBackfill.length === 0) {
            alert(`No records need backfilling!\n\n${recordsAlreadyHavePdf} records already have signed PDFs.`);
            return;
        }

        const confirmed = window.confirm(
            `Backfill Signed PDFs\n\n` +
            `• ${recordsToBackfill.length} records need signed PDFs\n` +
            `• ${recordsAlreadyHavePdf} records already have PDFs (will be SKIPPED)\n\n` +
            `Continue?`
        );

        if (!confirmed) return;
        await runPdfGeneration(recordsToBackfill, false);
    };

    const handleRegenerateAllPdfs = async () => {
        const recordsWithSignature = submissions.filter(s => s.signature);
        const recordsWithExistingPdf = recordsWithSignature.filter(s => s.signedPdfUrl);

        if (recordsWithSignature.length === 0) {
            alert("No records with signatures found.");
            return;
        }

        const confirmed = window.confirm(
            `Regenerate ALL Signed PDFs\n\n` +
            `• ${recordsWithSignature.length} total records with signatures\n` +
            `• ${recordsWithExistingPdf.length} existing PDFs will be backed up before replacing\n\n` +
            `Old PDFs are saved to signed-agreements/backups/ in case anything goes wrong.\n\n` +
            `Continue?`
        );

        if (!confirmed) return;
        await runPdfGeneration(recordsWithSignature, true);
    };

    const runPdfGeneration = async (records, backupExisting) => {
        setBackfillStatus({ running: true, progress: 'Starting...', count: 0, total: records.length });

        let successCount = 0;
        let errorCount = 0;
        let backedUp = 0;

        for (let i = 0; i < records.length; i++) {
            const record = records[i];
            setBackfillStatus(prev => ({
                ...prev,
                progress: `Processing ${record.name || record.id}...`,
                count: i + 1
            }));

            try {
                // Back up existing PDF before overwriting
                if (backupExisting && record.signedPdfPath) {
                    try {
                        const oldRef = ref(storage, record.signedPdfPath);
                        const oldUrl = await getDownloadURL(oldRef);
                        const oldBytes = await fetch(oldUrl).then(r => r.arrayBuffer());
                        const backupPath = `signed-agreements/backups/${record.id}_backup_${Date.now()}.pdf`;
                        const backupRef = ref(storage, backupPath);
                        await uploadBytes(backupRef, new Blob([oldBytes], { type: 'application/pdf' }));
                        backedUp++;
                    } catch (backupErr) {
                        console.warn(`Could not back up existing PDF for ${record.id}:`, backupErr);
                    }
                }

                const pdfBlob = await generateSignedPdfForRecord(record);
                if (!pdfBlob) {
                    errorCount++;
                    continue;
                }

                const fileName = `signed-agreements/signed_agreement_${record.id}.pdf`;
                const storageRef = ref(storage, fileName);
                await uploadBytes(storageRef, pdfBlob);
                const downloadUrl = await getDownloadURL(storageRef);

                await updateDoc(doc(db, "medical_providers", record.id), {
                    signedPdfUrl: downloadUrl,
                    signedPdfPath: fileName,
                    regeneratedAt: new Date(),
                });

                successCount++;
            } catch (error) {
                console.error(`Error processing ${record.id}:`, error);
                errorCount++;
            }
        }

        setBackfillStatus({ running: false, progress: '', count: 0, total: 0 });
        alert(
            `Complete!\n\n` +
            `✓ Successfully processed: ${successCount}\n` +
            (backedUp > 0 ? `📦 Old PDFs backed up: ${backedUp}\n` : '') +
            `✗ Errors: ${errorCount}`
        );
    };

    if (loading) {
        return (
            <div className="admin-dashboard">
                <div className="loading-container">
                    <div className="spinner"></div>
                    <p>Loading submissions...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="admin-dashboard">
            <div className="admin-header">
                <div className="header-content">
                    <h1>Stamp Medical Admin Dashboard</h1>
                </div>
                <div className="header-actions">
                    <button
                        className="add-contractor-btn"
                        onClick={() => setShowManualEntry(true)}
                        title="Manually register a paper-enrollment contractor">
                        ➕ Add Contractor
                    </button>
                    <button
                        className="backfill-btn"
                        onClick={handleBackfillSignedPdfs}
                        disabled={backfillStatus.running}>
                        {backfillStatus.running
                            ? `⏳ ${backfillStatus.count}/${backfillStatus.total}`
                            : '📄 Backfill Missing PDFs'}
                    </button>
                    <button
                        className="backfill-btn regenerate-btn"
                        onClick={handleRegenerateAllPdfs}
                        disabled={backfillStatus.running}>
                        🔄 Regenerate All PDFs
                    </button>
                    <button
                        className="logout-btn"
                        onClick={onLogout}>
                        Logout
                    </button>
                </div>
            </div>

            <DashboardStats submissions={submissions} />

            <DashboardControls
                searchTerm={searchTerm}
                onSearchTermChange={setSearchTerm}
                filter={filter}
                onFilterChange={setFilter}
                enrollmentFilter={enrollmentFilter}
                onEnrollmentFilterChange={setEnrollmentFilter}
            />

            <SubmissionsTable
                submissions={filteredSubmissions}
                canApproveFeeSchedule={canApproveFeeSchedule}
                approvingFeeSchedule={approvingFeeSchedule}
                onViewDetails={handleViewDetails}
                onViewDocuments={setDocumentsModal}
                onOpenActivity={openActivityModal}
                onDelete={handleDelete}
                onFeeScheduleApproval={handleFeeScheduleApproval}
            />

            {/* Toast Notification */}
            {toast && (
                <div className={`toast toast-${toast.type}`}>
                    {toast.type === 'success' ? '✓' : '✕'} {toast.message}
                </div>
            )}

            {selectedSubmission && (
                <ApplicationDetailsModal
                    submission={selectedSubmission}
                    onClose={closeModal}
                    onOpenActivity={(submission) => {
                        closeModal();
                        openActivityModal(submission);
                    }}
                    currentAdminEmail={currentAdminEmail}
                    showToast={showToast}
                    onSubmissionChange={setSelectedSubmission}
                    canApproveFeeSchedule={canApproveFeeSchedule}
                    approvingFeeSchedule={approvingFeeSchedule}
                    onFeeScheduleApproval={handleFeeScheduleApproval}
                />
            )}

            {documentsModal && (
                <DocumentsModal
                    submission={documentsModal}
                    onClose={() => setDocumentsModal(null)}
                    showToast={showToast}
                    onSubmissionChange={setDocumentsModal}
                    currentAdminEmail={currentAdminEmail}
                />
            )}

            {showManualEntry && (
                <ManualEntryModal
                    onClose={() => setShowManualEntry(false)}
                    currentAdminEmail={currentAdminEmail}
                    showToast={showToast}
                />
            )}

            {activityModal && (
                <ActivityLogModal
                    submission={activityModal}
                    onClose={closeActivityModal}
                    onEditApplication={(submission) => {
                        closeActivityModal();
                        handleViewDetails(submission);
                    }}
                    currentAdminEmail={currentAdminEmail}
                    showToast={showToast}
                    onSubmissionChange={setActivityModal}
                />
            )}
        </div>
    );
};

export default AdminDashboard;
